// MCP Security — Events page.
//
// Rebuilt 2026-10-12 to match the visual family of WAF Events
// (/v2/detection/waf_event). We reuse WAFEvent.js' table shell
// (tableHeadSx/tableBodySx, MUI Table primitives, colors.primary[50]
// row hover, 1px neutral[200] border + 8px radius container), its
// filter-chip bar typography, its V2PageHeader + SiteScopeGate pattern
// and its formatDate timestamp — so the two surfaces read as one page
// family.
//
// MCP-only columns are preserved alongside the WAF-standard ones:
//   Time · Hostname · Endpoint · Tool · Agent · Session · Outcome ·
//   HTTP · ms · IP · Country · Attack class
//
// Data wiring (unchanged from the previous build):
//   GET    /mcp/sites/:site_uid/events?hours=24&limit=500
//   GET    /mcp/saved-views?site_uid=...
//   POST   /mcp/saved-views
//   DELETE /mcp/saved-views/:id
// Deep-link URL params from Overview still work (range, outcome, tool,
// endpoint, agent, session, country, class).

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import {
  Stack,
  Box,
  Typography,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Button,
  Switch,
  Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  BookmarkBorderOutlined as BookmarkOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
} from "@mui/icons-material";

import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { EventsTimeRangePicker } from "../../../../common/Greens";
import { formatDate } from "../../../../../utils/format";
import colors from "../../../../../utils/colors";
import { eventSearchInputSx } from "../../detection/eventSearchInputSx";
import { tableHeadSx, tableBodySx } from "../../detection/WAFEvent.js";

import axios from "../../../../../utils/axios/v1/userAxios";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import McpEventDrawer from "./EventDrawer";

import tokens from "./_shared/tokens";
import { outcomePalette } from "./_shared/OutcomePill";
import AttackChips, { ATTACK_CLASS_LABEL } from "./_shared/AttackChips";
import CountryFlag from "./_shared/CountryFlag";

const brandBlue = colors.primary[300];

const hoursForPeriod = (p) =>
  (p === "1h" ? 1 : p === "7d" ? 168 : p === "30d" ? 720 : 24);

const OUTCOME_OPTS = [
  { v: "", label: "Any outcome" },
  { v: "pass", label: "pass" },
  { v: "tag", label: "tag" },
  { v: "block_new_tool", label: "block_new_tool" },
  { v: "scope_denied", label: "scope_denied" },
  { v: "session_cap", label: "session_cap" },
  { v: "session_integrity", label: "session_integrity" },
  { v: "rate_limit", label: "rate_limit" },
  { v: "drift_block", label: "drift_block" },
  { v: "prompt_injection_blocked", label: "prompt_injection_blocked" },
  { v: "response_too_large", label: "response_too_large" },
  { v: "credential_leak", label: "credential_leak" },
];

const ATTACK_CLASSES = Object.keys(ATTACK_CLASS_LABEL);

const FIELD_FILTERS = [
  { k: "endpoint_id", label: "Endpoint id", ph: "ep_01HNA…" },
  { k: "tool",        label: "Tool name",   ph: "search_tickets" },
  { k: "request_id",  label: "Request id",  ph: "req_…" },
  { k: "client_ip",   label: "Client IP",   ph: "203.0.113.*" },
  { k: "agent_id",    label: "Agent id",    ph: "ag_abc" },
  { k: "session_fp",  label: "Session fp",  ph: "abc12345" },
];

const ITEMS_PER_PAGE = 10;

// WAF Events uses a colored dot + text in its Status column. We mirror
// that shape here so MCP outcomes render in the same visual slot while
// keeping their own slug palette.
function OutcomeDotCell({ outcome }) {
  const p = outcomePalette(outcome);
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: p.fg,
        }}
      />
      <Typography sx={{ ...tableBodySx, color: colors.neutral[1000] }}>
        {p.label || outcome || "—"}
      </Typography>
    </Stack>
  );
}

export default function McpEvents() {
  const { effectiveSiteUid } = useGlobalSite();
  const [searchParams, setSearchParams] = useSearchParams();

  const [timeRange, setTimeRange] = useState("24h");
  const [customDateRange, setCustomDateRange] = useState([
    "2000-01-01T00:00:00",
    "2000-01-02T00:00:00",
  ]);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [drawerEvent, setDrawerEvent] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [outcome, setOutcome] = useState(searchParams.get("outcome") || "");
  const [http, setHttp] = useState("all");
  const [fieldFilters, setFieldFilters] = useState({
    endpoint_id: searchParams.get("endpoint_id") || "",
    tool:        searchParams.get("tool") || "",
    request_id:  "",
    client_ip:   searchParams.get("client_ip") || "",
    agent_id:    searchParams.get("agent_id") || "",
    session_fp:  "",
    country:     (searchParams.get("country") || "").toUpperCase(),
  });
  const [classFilter, setClassFilter] = useState(() => {
    const csv = searchParams.get("class") || "";
    return csv ? new Set(csv.split(",").filter(Boolean)) : new Set();
  });

  // Saved views live in Postgres (McpSavedView) — per-user or shared.
  const [savedViews, setSavedViews] = useState([]);
  const [savedViewsRefresh, setSavedViewsRefresh] = useState(0);

  // Pagination — same ITEMS_PER_PAGE grain as WAF Events; the backend
  // returns up to 500 rows and we client-paginate for row rendering
  // responsiveness.
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const hours = useMemo(() => hoursForPeriod(timeRange), [timeRange]);

  useEffect(() => {
    if (!effectiveSiteUid) { setRows([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const params = new URLSearchParams({ hours: String(hours), limit: "500" });
        if (outcome)                      params.set("outcome", outcome);
        if (fieldFilters.endpoint_id)     params.set("endpoint_id", fieldFilters.endpoint_id);
        if (fieldFilters.country)         params.set("country", fieldFilters.country);
        if (fieldFilters.agent_id)        params.set("agent_id", fieldFilters.agent_id);
        if (classFilter.size > 0)         params.set("class", Array.from(classFilter).join(","));
        const r = await axios.get(`mcp/sites/${effectiveSiteUid}/events?${params}`);
        if (cancelled) return;
        setRows(r.data?.items || []);
        setErr(null);
        setVisibleCount(ITEMS_PER_PAGE);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.message || e.message);
      }
    };
    load();
    const t = autoRefresh ? setInterval(load, 15 * 1000) : null;
    return () => { cancelled = true; if (t) clearInterval(t); };
  }, [
    effectiveSiteUid, hours, outcome,
    fieldFilters.endpoint_id, fieldFilters.country, fieldFilters.agent_id,
    classFilter, autoRefresh, refreshKey,
  ]);

  // Persist filters to URL so Overview deep-links still work.
  useEffect(() => {
    const p = new URLSearchParams();
    if (outcome)                   p.set("outcome", outcome);
    if (fieldFilters.endpoint_id)  p.set("endpoint_id", fieldFilters.endpoint_id);
    if (fieldFilters.tool)         p.set("tool", fieldFilters.tool);
    if (fieldFilters.client_ip)    p.set("client_ip", fieldFilters.client_ip);
    if (fieldFilters.country)      p.set("country", fieldFilters.country);
    if (fieldFilters.agent_id)     p.set("agent_id", fieldFilters.agent_id);
    if (classFilter.size > 0)      p.set("class", Array.from(classFilter).join(","));
    setSearchParams(p, { replace: true });
  }, [outcome, fieldFilters, classFilter, setSearchParams]);

  const hasFilters = !!(
    q || outcome || http !== "all" ||
    classFilter.size > 0 ||
    Object.values(fieldFilters).some(Boolean)
  );

  const clearFilters = () => {
    setQ(""); setOutcome(""); setHttp("all");
    setClassFilter(new Set());
    setFieldFilters({
      endpoint_id: "", tool: "", request_id: "",
      client_ip: "", agent_id: "", session_fp: "",
      country: "",
    });
  };

  const filtered = useMemo(() => {
    if (!rows) return null;
    return rows.filter((r) => {
      if (fieldFilters.tool       && (r.tool_name || "") !== fieldFilters.tool) return false;
      if (fieldFilters.client_ip  && !(r.client_ip || "").startsWith(fieldFilters.client_ip)) return false;
      if (fieldFilters.request_id && !(r.request_id || "").startsWith(fieldFilters.request_id)) return false;
      if (fieldFilters.agent_id   && (r.agent_id || "") !== fieldFilters.agent_id) return false;
      if (fieldFilters.session_fp && !(r.session_fp || "").startsWith(fieldFilters.session_fp)) return false;
      if (http === "2xx" && !(r.http_status >= 200 && r.http_status < 300)) return false;
      if (http === "4xx" && !(r.http_status >= 400 && r.http_status < 500)) return false;
      if (http === "5xx" && !(r.http_status >= 500 && r.http_status < 600)) return false;
      if (q) {
        const bag = [
          r.tool_name, r.client_ip, r.request_id, r.agent_id, r.user_agent, r.details,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!bag.includes(q.toLowerCase())) return false;
      }
      if (classFilter.size > 0) {
        let details = r.details;
        if (typeof details === "string" && details.length) {
          try { details = JSON.parse(details); } catch { details = null; }
        }
        if (!details) return false;
        const rowClasses = Object.keys(details);
        if (![...classFilter].some((c) => rowClasses.includes(c))) return false;
      }
      return true;
    });
  }, [rows, fieldFilters, http, q, classFilter]);

  const loading = rows === null;

  const toggleClass = (c) =>
    setClassFilter((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });

  // Saved views — load for current site.
  useEffect(() => {
    if (!effectiveSiteUid) return;
    let cancelled = false;
    axios.get(`mcp/saved-views?site_uid=${effectiveSiteUid}`)
      .then((r) => { if (!cancelled) setSavedViews(r.data?.items || []); })
      .catch(() => { if (!cancelled) setSavedViews([]); });
    return () => { cancelled = true; };
  }, [effectiveSiteUid, savedViewsRefresh]);

  const saveView = async () => {
    const name = window.prompt("Save view as", "Untitled view");
    if (!name || !effectiveSiteUid) return;
    const shared = window.confirm("Share this view with the rest of the site? (Cancel = keep it private.)");
    const filters = { outcome, http, q, fieldFilters, classFilter: [...classFilter] };
    try {
      await axios.post("mcp/saved-views", {
        site_uid: effectiveSiteUid, name, filters, shared,
      });
      setSavedViewsRefresh((k) => k + 1);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Save view failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const deleteView = async (v) => {
    if (!v?.id) return;
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try {
      await axios.delete(`mcp/saved-views/${v.id}`);
      setSavedViewsRefresh((k) => k + 1);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Delete view failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const applyView = (v) => {
    const f = v?.filters || v || {};
    setOutcome(f.outcome || "");
    setHttp(f.http || "all");
    setQ(f.q || "");
    setFieldFilters(f.fieldFilters || {
      endpoint_id: "", tool: "", request_id: "",
      client_ip: "", agent_id: "", session_fp: "",
      country: "",
    });
    setClassFilter(new Set(f.classFilter || []));
    const p = new URLSearchParams();
    if (f.outcome)                       p.set("outcome", f.outcome);
    if (f.fieldFilters?.endpoint_id)     p.set("endpoint_id", f.fieldFilters.endpoint_id);
    if (f.fieldFilters?.tool)            p.set("tool", f.fieldFilters.tool);
    if (f.fieldFilters?.client_ip)       p.set("client_ip", f.fieldFilters.client_ip);
    if (f.fieldFilters?.country)         p.set("country", f.fieldFilters.country);
    if (f.fieldFilters?.agent_id)        p.set("agent_id", f.fieldFilters.agent_id);
    if ((f.classFilter || []).length)    p.set("class", (f.classFilter || []).join(","));
    setSearchParams(p, { replace: true });
  };

  const exportCsv = () => {
    const cols = [
      "ts", "endpoint_id", "tool_name", "outcome", "http_status",
      "latency_ms", "client_ip", "country", "agent_id", "model_id",
      "tokens_in", "tokens_out", "session_fp", "request_id",
    ];
    const header = cols.join(",") + "\n";
    const body = (filtered || []).map((r) =>
      cols
        .map((k) => (r[k] == null ? "" : String(r[k]).replace(/"/g, '""')))
        .map((x) => `"${x}"`)
        .join(",")
    ).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mcp-events-${new Date().toISOString().slice(0, 19)}.csv`;
    a.click();
  };

  const exportJson = () => {
    const body = (filtered || []).map((r) => JSON.stringify(r)).join("\n");
    const blob = new Blob([body], { type: "application/x-ndjson" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mcp-events-${new Date().toISOString().slice(0, 19)}.jsonl`;
    a.click();
  };

  const rowsForTable = (filtered || []).slice(0, visibleCount);
  const totalCount = (filtered || []).length;
  const hasMoreToLoad = totalCount > rowsForTable.length;

  // ── Filter chip bar — WAF visual palette ──────────────────────────
  const chipRowBoxSx = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 1,
  };

  const smallSelectSx = {
    height: 36,
    minWidth: 150,
    fontSize: 14,
    borderRadius: "8px",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[200] },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: brandBlue },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brandBlue },
  };

  return (
    <>
      <Helmet title="MCP Event" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Event"
          sx={{ pb: 0 }}
          actions={(
            <Stack direction="row" spacing={1.5} alignItems="center">
              <EventsTimeRangePicker
                selectPeriod={setTimeRange}
                selectCustomDateRange={setCustomDateRange}
                timeRange={timeRange}
                customDateRange={customDateRange}
              />
              <Tooltip title="Refresh now">
                <IconButton
                  size="small"
                  onClick={() => setRefreshKey((k) => k + 1)}
                  sx={{
                    border: `1px solid ${colors.neutral[200]}`,
                    borderRadius: "8px",
                    width: 36, height: 36,
                    "&:hover": { borderColor: brandBlue, backgroundColor: "rgba(99,113,247,0.08)" },
                  }}
                >
                  <RefreshOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && (
                <Alert severity="info">Select a site from the header to see MCP events.</Alert>
              )}

              {/* ── Filter chip bar (WAF visual palette, MCP-specific chips) ── */}
              <Stack spacing={2}>
                <Box sx={chipRowBoxSx}>
                  <TextField
                    placeholder="Search tool_name, client_ip, request_id, agent_id, details"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    size="small"
                    sx={eventSearchInputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" className="event-search-icon"
                            sx={{ color: colors.neutral[600] }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Select
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    size="small"
                    displayEmpty
                    sx={smallSelectSx}
                  >
                    {OUTCOME_OPTS.map((o) => (
                      <MenuItem key={o.v || "_all"} value={o.v}>{o.label}</MenuItem>
                    ))}
                  </Select>
                  <Select
                    value={http}
                    onChange={(e) => setHttp(e.target.value)}
                    size="small"
                    sx={{ ...smallSelectSx, minWidth: 110 }}
                  >
                    <MenuItem value="all">Any HTTP</MenuItem>
                    <MenuItem value="2xx">2xx</MenuItem>
                    <MenuItem value="4xx">4xx</MenuItem>
                    <MenuItem value="5xx">5xx</MenuItem>
                  </Select>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<BookmarkOutlinedIcon />}
                    onClick={saveView}
                    sx={{
                      height: 36,
                      borderRadius: "8px",
                      textTransform: "none",
                      borderColor: colors.neutral[200],
                      color: colors.neutral[1000],
                      backgroundColor: "#fff",
                      "&:hover": {
                        borderColor: brandBlue,
                        backgroundColor: "rgba(99,113,247,0.08)",
                      },
                    }}
                  >
                    Save view
                  </Button>
                  {hasFilters && (
                    <Button
                      size="small"
                      onClick={clearFilters}
                      sx={{
                        textTransform: "none",
                        color: brandBlue,
                        fontWeight: 500,
                        "&:hover": { backgroundColor: "rgba(99,113,247,0.08)" },
                      }}
                    >
                      Clear all
                    </Button>
                  )}
                </Box>

                {/* Field-filter grid (MCP-only; styled to match WAF filter row shape) */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1 }}>
                  {FIELD_FILTERS.map((f) => (
                    <Stack key={f.k} spacing={0.25}>
                      <Typography sx={{ fontSize: 11, fontWeight: 500, color: colors.neutral[700], letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        {f.label}
                      </Typography>
                      <TextField
                        value={fieldFilters[f.k]}
                        onChange={(e) => setFieldFilters((prev) => ({ ...prev, [f.k]: e.target.value }))}
                        placeholder={f.ph}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 34,
                            fontSize: 13,
                            fontFamily: tokens.fontMono,
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            "& fieldset": { borderColor: colors.neutral[200] },
                            "&:hover fieldset": { borderColor: brandBlue },
                            "&.Mui-focused fieldset": { borderColor: brandBlue },
                          },
                        }}
                      />
                    </Stack>
                  ))}
                </Box>

                {/* Attack-class chip row — MCP-only, same shape as WAF chip rows */}
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontSize: 11, fontWeight: 500, color: colors.neutral[700], letterSpacing: "0.04em", textTransform: "uppercase", mr: 0.5 }}>
                    Attack class
                  </Typography>
                  {ATTACK_CLASSES.map((c) => {
                    const on = classFilter.has(c);
                    return (
                      <Box
                        key={c}
                        onClick={() => toggleClass(c)}
                        sx={{
                          fontFamily: tokens.fontMono,
                          fontSize: 11,
                          padding: "3px 10px",
                          borderRadius: "999px",
                          cursor: "pointer",
                          border: `1px solid ${on ? brandBlue : colors.neutral[200]}`,
                          background: on ? "rgba(99,113,247,0.08)" : "#fff",
                          color: on ? brandBlue : colors.neutral[1000],
                          transition: "all 140ms ease",
                          "&:hover": {
                            borderColor: brandBlue,
                            backgroundColor: "rgba(99,113,247,0.08)",
                          },
                        }}
                      >
                        {ATTACK_CLASS_LABEL[c] || c}
                      </Box>
                    );
                  })}
                </Stack>

                {savedViews.length > 0 && (
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: 11, fontWeight: 500, color: colors.neutral[700], letterSpacing: "0.04em", textTransform: "uppercase", mr: 0.5 }}>
                      Saved views
                    </Typography>
                    {savedViews.map((v) => (
                      <Box
                        key={v.id || v.name}
                        title={v.shared ? "Shared with the site" : (v.mine ? "Your private view" : "")}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: 12,
                          padding: "3px 4px 3px 10px",
                          borderRadius: "999px",
                          background: v.shared ? colors.neutral[100] : "rgba(99,113,247,0.10)",
                          color: v.shared ? colors.neutral[1000] : brandBlue,
                          border: `1px solid ${v.shared ? colors.neutral[200] : "rgba(99,113,247,0.30)"}`,
                        }}
                      >
                        <Box
                          onClick={() => applyView(v)}
                          sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 0.5 }}
                        >
                          <BookmarkOutlinedIcon sx={{ fontSize: 12 }} />
                          {v.name || v.label}
                        </Box>
                        {(v.mine || v.shared) && (
                          <Box
                            onClick={() => deleteView(v)}
                            title="Delete"
                            sx={{
                              cursor: "pointer",
                              fontSize: 11,
                              padding: "0 6px",
                              borderRadius: "999px",
                              color: colors.neutral[700],
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
                            }}
                          >
                            ×
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>

              {err && <Alert severity="error">{err}</Alert>}

              {/* ── Table (WAF shell) ── */}
              <Stack>
                <Box sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px", p: 2, overflowX: "auto" }}>
                  <Table
                    sx={{
                      minWidth: 1280,
                      "& .MuiTableCell-head": { py: 3, px: 2 },
                      "& .MuiTableCell-body": { py: 2, px: 2 },
                      "& .MuiTableCell-root": { borderBottom: "none" },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 170 }}><Typography sx={tableHeadSx}>Timestamp</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>Hostname</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>Endpoint</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>Tool</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>Agent</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>Session</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>Outcome</Typography></TableCell>
                        <TableCell sx={{ width: 72 }}><Typography sx={tableHeadSx}>HTTP</Typography></TableCell>
                        <TableCell sx={{ width: 72 }}><Typography sx={tableHeadSx}>ms</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>IP</Typography></TableCell>
                        <TableCell sx={{ width: 86 }}><Typography sx={tableHeadSx}>Country</Typography></TableCell>
                        <TableCell><Typography sx={tableHeadSx}>Attack class</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    {loading ? (
                      <TableBody>
                        {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                          <TableRow key={`sk_${i}`}>
                            {Array.from({ length: 12 }).map((_, j) => (
                              <TableCell key={j} sx={{ py: 2, px: 2 }}>
                                <Skeleton variant="rectangular" height={18} sx={{ borderRadius: "6px" }} />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    ) : rowsForTable.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={12}>
                            <Typography textAlign="center" sx={tableBodySx}>
                              No events match these filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : (
                      <TableBody>
                        {rowsForTable.map((r, index) => {
                          let details = r.details;
                          if (typeof details === "string" && details.length) {
                            try { details = JSON.parse(details); } catch { details = null; }
                          }
                          const classes = details && typeof details === "object"
                            ? Object.keys(details) : [];
                          return (
                            <TableRow
                              key={r.request_id || `mcp_row_${index}`}
                              onClick={() => setDrawerEvent(r)}
                              sx={{
                                cursor: "pointer",
                                "&:hover": { backgroundColor: colors.primary[50] },
                              }}
                            >
                              <TableCell>
                                <Typography sx={tableBodySx}>{formatDate(r.ts) || "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={tableBodySx}>
                                  {r.host || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    ...tableBodySx,
                                    fontFamily: tokens.fontMono,
                                    color: brandBlue,
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {r.endpoint_id ? r.endpoint_id.slice(0, 14) : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    ...tableBodySx,
                                    fontFamily: tokens.fontMono,
                                  }}
                                >
                                  {r.tool_name || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={tableBodySx}>{r.agent_id || "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    ...tableBodySx,
                                    fontFamily: tokens.fontMono,
                                    color: brandBlue,
                                  }}
                                >
                                  {r.session_fp ? r.session_fp.slice(0, 8) : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <OutcomeDotCell outcome={r.outcome} />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    ...tableBodySx,
                                    fontFamily: tokens.fontMono,
                                    color: r.http_status >= 400 ? "#B91C1C" : colors.neutral[800],
                                  }}
                                >
                                  {r.http_status || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    ...tableBodySx,
                                    fontFamily: tokens.fontMono,
                                  }}
                                >
                                  {r.latency_ms ?? "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    ...tableBodySx,
                                    fontFamily: tokens.fontMono,
                                  }}
                                >
                                  {r.client_ip || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <CountryFlag cc={r.country || ""} showCode />
                              </TableCell>
                              <TableCell>
                                <AttackChips classes={classes} dense limit={3} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    )}
                  </Table>

                  {!loading && hasMoreToLoad && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <Button
                        size="small"
                        onClick={() => setVisibleCount((n) => n + ITEMS_PER_PAGE)}
                        sx={{
                          textTransform: "none",
                          color: brandBlue,
                          "&:hover": { backgroundColor: "rgba(99,113,247,0.08)" },
                        }}
                      >
                        Load more
                      </Button>
                    </Box>
                  )}
                  {!loading && !hasMoreToLoad && totalCount > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>
                        No more events to load
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Footer actions — count, auto-refresh toggle, CSV/JSON export */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 2 }}
                >
                  <Typography sx={{ ...tableBodySx }}>
                    {loading ? "loading…" : `${totalCount} events`}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
                    Click a row for detail.
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Switch
                      size="small"
                      checked={autoRefresh}
                      onChange={() => setAutoRefresh((v) => !v)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: brandBlue },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: brandBlue },
                      }}
                    />
                    <Typography sx={{ fontSize: 13 }}>Auto-refresh 15 s</Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={exportCsv}
                    sx={{
                      textTransform: "none",
                      height: 32,
                      borderRadius: "8px",
                      borderColor: colors.neutral[200],
                      color: colors.neutral[1000],
                      "&:hover": { borderColor: brandBlue, backgroundColor: "rgba(99,113,247,0.08)" },
                    }}
                  >
                    CSV
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={exportJson}
                    sx={{
                      textTransform: "none",
                      height: 32,
                      borderRadius: "8px",
                      borderColor: colors.neutral[200],
                      color: colors.neutral[1000],
                      "&:hover": { borderColor: brandBlue, backgroundColor: "rgba(99,113,247,0.08)" },
                    }}
                  >
                    JSON
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      <McpEventDrawer
        open={!!drawerEvent}
        event={drawerEvent}
        siteUid={effectiveSiteUid}
        onClose={() => setDrawerEvent(null)}
      />
    </>
  );
}
